import { decryptField, encryptField } from '../crypto/aesgcm';
import type { CruzFindVault, SubscriberProfileId, SubscriberProfileRow } from './schema';
import type { SubscriberProfile } from '../../types/subscriber';

const ROW_ID: SubscriberProfileId = 'singleton';

export class SubscriberProfileRepo {
  constructor(
    private readonly db: CruzFindVault,
    private readonly aesKey: CryptoKey,
  ) {}

  async get(): Promise<SubscriberProfile | null> {
    const row = await this.db.subscriber_profile.get(ROW_ID);
    if (!row) return null;
    return this.decryptRow(row);
  }

  async save(input: SubscriberProfile, now: number = Date.now()): Promise<SubscriberProfile> {
    // RMC 77-2024 immutability: once a prior save has set isLocked=true, the
    // businessName and businessTin become permanent. Any further save() — even
    // with hostile inputs from a manipulated form state — silently preserves
    // the originally-locked values. isLocked is also monotonic.
    const existing = await this.db.subscriber_profile.get(ROW_ID);
    const existingDecrypted = existing ? await this.decryptRow(existing) : null;
    const wasLocked = existingDecrypted?.isLocked === true;

    // Calculate statutory Philippine business permit deadline (January 20 of next year)
    const currentYear = new Date(now).getFullYear();
    const statutoryDeadline = new Date(currentYear + 1, 0, 20, 23, 59, 59, 999).getTime();
    const effectiveExpirationDate =
      existing?.accountExpirationDate ?? input.accountExpirationDate ?? statutoryDeadline;

    const effectiveBusinessName =
      wasLocked && existingDecrypted ? existingDecrypted.businessName : input.businessName;
    const effectiveBusinessTin =
      wasLocked && existingDecrypted ? existingDecrypted.businessTin : input.businessTin;
    const effectiveIsLocked = wasLocked ? true : input.isLocked === true;

    const [encryptedBusinessName, encryptedBusinessAddress] = await Promise.all([
      encryptField(this.aesKey, effectiveBusinessName),
      encryptField(this.aesKey, input.businessAddress),
    ]);
    const encryptedBusinessTin =
      effectiveBusinessTin && effectiveBusinessTin.length > 0
        ? await encryptField(this.aesKey, effectiveBusinessTin)
        : undefined;
    // Entity-class registration numbers are mutually exclusive — only the field
    // matching `entityClass` is persisted.
    const dtiInput =
      input.entityClass === 'DTI' && input.dtiRegistrationNumber
        ? input.dtiRegistrationNumber
        : undefined;
    const secInput =
      input.entityClass === 'SEC' && input.secRegistrationNumber
        ? input.secRegistrationNumber
        : undefined;
    const encryptedDtiRegistrationNumber =
      dtiInput && dtiInput.length > 0
        ? await encryptField(this.aesKey, dtiInput)
        : undefined;
    const encryptedSecRegistrationNumber =
      secInput && secInput.length > 0
        ? await encryptField(this.aesKey, secInput)
        : undefined;
    const encryptedLogoBase64 =
      input.logoBase64 && input.logoBase64.length > 0
        ? await encryptField(this.aesKey, input.logoBase64)
        : undefined;
    const row: SubscriberProfileRow = {
      id: ROW_ID,
      encryptedBusinessName,
      encryptedBusinessAddress,
      isRegistered: input.isRegistered,
      encryptedBusinessTin,
      entityClass: input.entityClass,
      encryptedDtiRegistrationNumber,
      encryptedSecRegistrationNumber,
      encryptedLogoBase64,
      isLocked: effectiveIsLocked,
      accountExpirationDate: effectiveExpirationDate,
      updatedAt: now,
    };
    await this.db.subscriber_profile.put(row);
    return {
      businessName: effectiveBusinessName,
      businessAddress: input.businessAddress,
      isRegistered: input.isRegistered,
      businessTin: effectiveBusinessTin?.length ? effectiveBusinessTin : undefined,
      entityClass: input.entityClass,
      dtiRegistrationNumber: dtiInput,
      secRegistrationNumber: secInput,
      logoBase64: input.logoBase64?.length ? input.logoBase64 : undefined,
      isLocked: effectiveIsLocked,
      accountExpirationDate: effectiveExpirationDate,
    };
  }

  async clear(): Promise<void> {
    await this.db.subscriber_profile.delete(ROW_ID);
  }

  private async decryptRow(row: SubscriberProfileRow): Promise<SubscriberProfile> {
    const [businessName, businessAddress] = await Promise.all([
      decryptField(this.aesKey, row.encryptedBusinessName),
      decryptField(this.aesKey, row.encryptedBusinessAddress),
    ]);
    const businessTin = row.encryptedBusinessTin
      ? await decryptField(this.aesKey, row.encryptedBusinessTin)
      : undefined;
    const dtiRegistrationNumber = row.encryptedDtiRegistrationNumber
      ? await decryptField(this.aesKey, row.encryptedDtiRegistrationNumber)
      : undefined;
    const secRegistrationNumber = row.encryptedSecRegistrationNumber
      ? await decryptField(this.aesKey, row.encryptedSecRegistrationNumber)
      : undefined;
    const logoBase64 = row.encryptedLogoBase64
      ? await decryptField(this.aesKey, row.encryptedLogoBase64)
      : undefined;
    return {
      businessName,
      businessAddress,
      isRegistered: row.isRegistered,
      businessTin,
      entityClass: row.entityClass,
      dtiRegistrationNumber,
      secRegistrationNumber,
      logoBase64,
      isLocked: row.isLocked === true,
      accountExpirationDate: row.accountExpirationDate,
    };
  }
}
